/**
 * ProJobDetail — mobile-first job page with tabs.
 * Tabs: Overview · Schedule · Materials · Diary · Photos · Variations · Activity
 * Payments / Warranty / Reviews land in Phase 3+ and are not shown here yet.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Camera, CalendarClock, ClipboardList, FileText,
  Hammer, History, ListChecks, Plus, RefreshCw, Sparkles, AlertCircle,
  Receipt, CheckCheck, Send, Trash2, ShieldCheck, ClipboardCheck, Download,
} from "lucide-react";
import api from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const STATUS_ORDER = [
  "pending_conversion", "scheduled", "in_progress",
  "completed", "warranty_active", "closed", "cancelled",
];

const STATUS_LABEL = {
  pending_conversion: "Pending",
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  warranty_active: "Warranty active",
  closed: "Closed",
  cancelled: "Cancelled",
};

const STATUS_STYLES = {
  pending_conversion: "bg-brand-linen text-brand-ink",
  scheduled: "bg-brand-teal/15 text-brand-teal",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  warranty_active: "bg-sky-100 text-sky-800",
  closed: "bg-stone-200 text-stone-700",
  cancelled: "bg-rose-100 text-rose-800",
};

const TABS = [
  { id: "overview", label: "Overview", icon: Sparkles },
  { id: "schedule", label: "Schedule", icon: CalendarClock },
  { id: "materials", label: "Materials", icon: ClipboardList },
  { id: "diary", label: "Diary", icon: FileText },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "variations", label: "Variations", icon: ListChecks },
  { id: "payments", label: "Payments", icon: Receipt },
  { id: "completion", label: "Completion", icon: ClipboardCheck },
  { id: "warranty", label: "Warranty", icon: ShieldCheck },
  { id: "activity", label: "Activity", icon: History },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function ProJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  usePageMeta({ title: `Job · ColourMatch Pro`, path: `/pro/jobs/${id}` });

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/pro/jobs/${id}`);
      setJob(r.data?.job);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load job");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const validNext = useMemo(() => {
    if (!job) return [];
    const T = {
      pending_conversion: ["scheduled", "in_progress", "cancelled"],
      scheduled: ["in_progress", "cancelled", "pending_conversion"],
      in_progress: ["completed", "cancelled", "scheduled"],
      completed: ["warranty_active", "closed", "in_progress"],
      warranty_active: ["closed", "completed"],
      closed: ["warranty_active"],
      cancelled: ["pending_conversion"],
    };
    return T[job.status] || [];
  }, [job]);

  async function changeStatus(to) {
    const reason = to === "cancelled" ? window.prompt("Cancellation reason (min 10 chars)") : null;
    if (to === "cancelled" && (!reason || reason.trim().length < 10)) return;
    try {
      const r = await api.post(`/pro/jobs/${id}/status`, {
        to_status: to,
        reason: reason || undefined,
      });
      setJob(r.data?.job);
    } catch (e) {
      alert(e?.response?.data?.detail || "Status change failed");
    }
  }

  if (loading && !job) {
    return <div className="p-8 text-center text-sm text-brand-muted" data-testid="job-detail-loading">Loading job…</div>;
  }
  if (error && !job) {
    return (
      <div className="p-8 border border-rose-200 bg-rose-50 text-rose-900" data-testid="job-detail-error">
        <AlertCircle className="w-4 h-4 mb-1 inline" /> {error}
        <div className="mt-4"><Link to="/pro/jobs" className="text-sm underline">Back to jobs</Link></div>
      </div>
    );
  }
  if (!job) return null;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      {/* Header */}
      <div>
        <button
          onClick={() => nav("/pro/jobs")}
          data-testid="job-detail-back"
          className="text-xs text-brand-muted hover:text-brand-ink inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3 h-3" /> Back to jobs
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-xs text-brand-muted" data-testid="job-detail-number">{job.job_number}</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wide font-medium ${STATUS_STYLES[job.status] || "bg-stone-100 text-stone-700"}`}
                data-testid="job-detail-status-badge"
              >
                {STATUS_LABEL[job.status] || job.status}
              </span>
            </div>
            <h1 className="font-serif text-2xl sm:text-3xl text-brand-ink" data-testid="job-detail-title">
              {job.title || "Untitled job"}
            </h1>
            <div className="text-sm text-brand-muted mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span>{job.client_name || "No client"}</span>
              {job.client_email && <span>{job.client_email}</span>}
              {job.client_phone && <span>{job.client_phone}</span>}
            </div>
          </div>
          {job.outstanding_balance_aud > 0 && (
            <div className="text-right" data-testid="job-detail-outstanding">
              <div className="overline text-brand-muted mb-1">Outstanding</div>
              <div className="font-serif text-2xl text-brand-ink">${Number(job.outstanding_balance_aud).toFixed(2)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Status transitions */}
      {validNext.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="job-detail-status-actions">
          <span className="text-xs text-brand-muted">Move to:</span>
          {validNext.map((s) => (
            <button
              key={s}
              onClick={() => changeStatus(s)}
              data-testid={`job-status-change-${s}`}
              className="px-3 h-8 text-xs bg-white border border-brand-linen hover:border-brand-teal transition-colors"
            >
              {STATUS_LABEL[s] || s}
            </button>
          ))}
        </div>
      )}

      {/* Tabs — horizontal scroll on mobile */}
      <div className="border-b border-brand-linen overflow-x-auto" data-testid="job-detail-tabs">
        <div className="flex min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`job-detail-tab-${t.id}`}
                className={`px-4 h-11 text-sm inline-flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
                  active ? "border-brand-teal text-brand-teal" : "border-transparent text-brand-muted hover:text-brand-ink"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {tab === "overview" && <OverviewTab job={job} onReload={reload} />}
      {tab === "schedule" && <ScheduleTab job={job} onReload={reload} />}
      {tab === "materials" && <MaterialsTab job={job} onReload={reload} />}
      {tab === "diary" && <DiaryTab job={job} />}
      {tab === "photos" && <PhotosTab job={job} />}
      {tab === "variations" && <VariationsTab job={job} />}
      {tab === "payments" && <PaymentsTab job={job} onReload={reload} />}
      {tab === "completion" && <CompletionTab job={job} onReload={reload} />}
      {tab === "warranty" && <WarrantyTab job={job} onReload={reload} />}
      {tab === "activity" && <ActivityTab job={job} />}
    </div>
  );
}

// -- Overview ---------------------------------------------------------------
function OverviewTab({ job }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="job-tab-overview">
      <Card title="Client">
        <Row label="Name" value={job.client_name || "—"} />
        <Row label="Email" value={job.client_email || "—"} />
        <Row label="Phone" value={job.client_phone || "—"} />
        <Row label="Site" value={job.site_address || "—"} />
      </Card>
      <Card title="Quote">
        <Row label="Quote number" value={job.quote_number || "—"} />
        <Row label="Quoted total" value={`$${Number(job.quoted_total_aud || 0).toFixed(2)} inc. GST`} />
        <Row label="Converted" value={fmtDateTime(job.converted_from_quote_at)} />
      </Card>
      <Card title="Counts">
        <Row label="Materials" value={String(job.counts?.materials ?? 0)} />
        <Row label="Diary entries" value={String(job.counts?.diary ?? 0)} />
        <Row label="Photos" value={String(job.counts?.photos ?? 0)} />
        <Row label="Variations" value={String(job.counts?.variations ?? 0)} />
      </Card>
      <Card title="Internal notes">
        <div className="text-sm text-brand-ink whitespace-pre-wrap" data-testid="job-overview-notes">
          {job.internal_notes || <span className="text-brand-muted">No notes yet.</span>}
        </div>
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-brand-linen p-5">
      <div className="overline text-brand-muted mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-brand-muted">{label}</span>
      <span className="text-brand-ink text-right break-words">{value}</span>
    </div>
  );
}

// -- Schedule ---------------------------------------------------------------
function ScheduleTab({ job, onReload }) {
  const [form, setForm] = useState({
    scheduled_start_date: job.scheduled_start_date || "",
    target_completion_date: job.target_completion_date || "",
    actual_start_date: job.actual_start_date || "",
    actual_completion_date: job.actual_completion_date || "",
    internal_notes: job.internal_notes || "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/pro/jobs/${job.id}`, form);
      await onReload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="job-tab-schedule">
      <Field label="Scheduled start"
        value={form.scheduled_start_date}
        onChange={(v) => setForm({ ...form, scheduled_start_date: v })}
        type="date" testid="job-schedule-start" />
      <Field label="Target completion"
        value={form.target_completion_date}
        onChange={(v) => setForm({ ...form, target_completion_date: v })}
        type="date" testid="job-schedule-target" />
      <Field label="Actual start"
        value={form.actual_start_date}
        onChange={(v) => setForm({ ...form, actual_start_date: v })}
        type="date" testid="job-schedule-actual-start" />
      <Field label="Actual completion"
        value={form.actual_completion_date}
        onChange={(v) => setForm({ ...form, actual_completion_date: v })}
        type="date" testid="job-schedule-actual-completion" />
      <div className="md:col-span-2">
        <label className="block text-xs text-brand-muted mb-1">Internal notes</label>
        <textarea
          value={form.internal_notes}
          onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
          rows={4}
          data-testid="job-schedule-notes"
          className="w-full p-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal"
        />
      </div>
      <div className="md:col-span-2">
        <button
          onClick={save}
          disabled={saving}
          data-testid="job-schedule-save-btn"
          className="px-5 h-10 bg-brand-navy text-white text-sm hover:bg-brand-navy/90 disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving…" : "Save schedule"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs text-brand-muted mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 px-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal"
      />
    </div>
  );
}

// -- Materials --------------------------------------------------------------
function MaterialsTab({ job, onReload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.get(`/pro/jobs/${job.id}/materials`);
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  async function seedFromScheme() {
    try {
      const r = await api.post(`/pro/jobs/${job.id}/materials/from-scheme`);
      alert(`${r.data?.seeded || 0} materials seeded from the approved Colour Schedule.`);
      await reload();
      await onReload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Seed from scheme failed");
    }
  }

  return (
    <div className="space-y-4" data-testid="job-tab-materials">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setShowAdd(true)}
          data-testid="job-material-add-btn"
          className="inline-flex items-center gap-1 px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90"
        >
          <Plus className="w-4 h-4" /> Add material
        </button>
        {job.approved_scheme_id && (
          <button
            onClick={seedFromScheme}
            data-testid="job-material-seed-from-scheme-btn"
            className="inline-flex items-center gap-1 px-4 h-9 text-sm bg-white border border-brand-linen hover:border-brand-teal"
          >
            <RefreshCw className="w-4 h-4" /> Seed from Colour Schedule
          </button>
        )}
      </div>

      {showAdd && (
        <AddMaterialForm jobId={job.id} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); onReload(); }} />
      )}

      {loading ? (
        <div className="text-sm text-brand-muted">Loading materials…</div>
      ) : items.length === 0 ? (
        <EmptyState label="No materials yet." />
      ) : (
        <div className="border border-brand-linen bg-white overflow-x-auto" data-testid="job-materials-table">
          <table className="w-full text-sm">
            <thead className="text-xs text-brand-muted border-b border-brand-linen">
              <tr>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Product</th>
                <th className="text-left p-3">Surface</th>
                <th className="text-left p-3">Colour</th>
                <th className="text-right p-3">Qty</th>
                <th className="text-right p-3">Price</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-brand-linen/60" data-testid={`job-material-row-${m.id}`}>
                  <td className="p-3 text-xs">
                    {m.source === "colour_schedule" ? (
                      <span className="inline-flex items-center gap-1 text-brand-teal">
                        <Sparkles className="w-3 h-3" /> Schedule
                      </span>
                    ) : "Manual"}
                  </td>
                  <td className="p-3">{m.product_name || "—"}</td>
                  <td className="p-3 text-xs text-brand-muted">{m.surface_slug || "—"}</td>
                  <td className="p-3 font-mono text-xs">{m.library_code || "—"}</td>
                  <td className="p-3 text-right">{m.quantity ?? "—"} {m.quantity != null ? (m.unit || "") : ""}</td>
                  <td className="p-3 text-right">{m.unit_price_aud != null ? `$${Number(m.unit_price_aud).toFixed(2)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AddMaterialForm({ jobId, onClose, onSaved }) {
  const [f, setF] = useState({
    source: "manual", product_name: "", brand: "", finish: "",
    quantity: "", unit: "L", supplier: "", unit_price_aud: "", gst_inclusive: true, notes: "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...f };
      payload.quantity = payload.quantity === "" ? null : Number(payload.quantity);
      payload.unit_price_aud = payload.unit_price_aud === "" ? null : Number(payload.unit_price_aud);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post(`/pro/jobs/${jobId}/materials`, payload);
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white border border-brand-linen p-5 space-y-3" data-testid="job-material-add-form">
      <div className="overline text-brand-muted">New material</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Product name" value={f.product_name} onChange={(v) => setF({ ...f, product_name: v })} testid="mat-product" />
        <Field label="Brand" value={f.brand} onChange={(v) => setF({ ...f, brand: v })} testid="mat-brand" />
        <Field label="Finish" value={f.finish} onChange={(v) => setF({ ...f, finish: v })} testid="mat-finish" />
        <Field label="Supplier" value={f.supplier} onChange={(v) => setF({ ...f, supplier: v })} testid="mat-supplier" />
        <Field label="Quantity" value={f.quantity} onChange={(v) => setF({ ...f, quantity: v })} type="number" testid="mat-qty" />
        <div>
          <label className="block text-xs text-brand-muted mb-1">Unit</label>
          <select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}
            data-testid="mat-unit"
            className="w-full h-10 px-3 text-sm bg-white border border-brand-linen">
            <option value="L">L</option><option value="kg">kg</option>
            <option value="ea">each</option><option value="m2">m²</option>
          </select>
        </div>
        <Field label="Unit price (AUD)" value={f.unit_price_aud} onChange={(v) => setF({ ...f, unit_price_aud: v })} type="number" testid="mat-price" />
        <label className="inline-flex items-center gap-2 text-sm mt-6">
          <input type="checkbox" checked={f.gst_inclusive} onChange={(e) => setF({ ...f, gst_inclusive: e.target.checked })}
            data-testid="mat-gst-inclusive" />
          GST inclusive
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="mat-save-btn"
          className="px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50">
          {saving ? "Saving…" : "Save material"}
        </button>
        <button onClick={onClose} data-testid="mat-cancel-btn"
          className="px-4 h-9 text-sm bg-white border border-brand-linen hover:border-brand-teal">Cancel</button>
      </div>
    </div>
  );
}

// -- Diary ------------------------------------------------------------------
function DiaryTab({ job }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [weather, setWeather] = useState("");
  const [saving, setSaving] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.get(`/pro/jobs/${job.id}/diary`);
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  async function add() {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/pro/jobs/${job.id}/diary`, { note: note.trim(), weather: weather.trim() || undefined });
      setNote(""); setWeather("");
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="job-tab-diary">
      <div className="bg-white border border-brand-linen p-4 space-y-3">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="What happened on site today?"
          data-testid="job-diary-note"
          className="w-full p-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal"
        />
        <div className="flex flex-wrap gap-2 items-end">
          <input
            value={weather}
            onChange={(e) => setWeather(e.target.value)}
            placeholder="Weather (optional)"
            data-testid="job-diary-weather"
            className="h-9 px-3 text-sm bg-white border border-brand-linen flex-1 min-w-[160px]"
          />
          <button onClick={add} disabled={saving || !note.trim()}
            data-testid="job-diary-add-btn"
            className="inline-flex items-center gap-1 px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50">
            <Plus className="w-4 h-4" /> Add entry
          </button>
        </div>
      </div>

      {loading ? <div className="text-sm text-brand-muted">Loading…</div> :
        items.length === 0 ? <EmptyState label="No diary entries yet." /> : (
          <div className="space-y-2" data-testid="job-diary-entries">
            {items.map((e) => (
              <div key={e.id} className="bg-white border border-brand-linen p-4" data-testid={`job-diary-entry-${e.id}`}>
                <div className="text-xs text-brand-muted mb-1 flex flex-wrap gap-x-3">
                  <span>{fmtDate(e.entry_date)}</span>
                  {e.weather && <span>· {e.weather}</span>}
                </div>
                <div className="text-sm text-brand-ink whitespace-pre-wrap">{e.note}</div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// -- Photos -----------------------------------------------------------------
function PhotosTab({ job }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tag, setTag] = useState("progress");
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.get(`/pro/jobs/${job.id}/photos`);
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Max 5MB per photo."); return; }
    setSaving(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/pro/jobs/${job.id}/photos`, { url: dataUrl, tag, caption: caption || undefined });
      setCaption("");
      await reload();
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); e.target.value = ""; }
  }

  return (
    <div className="space-y-4" data-testid="job-tab-photos">
      <div className="bg-white border border-brand-linen p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={tag} onChange={(e) => setTag(e.target.value)}
            data-testid="job-photo-tag" className="h-9 px-3 text-sm bg-white border border-brand-linen">
            <option value="before">Before</option>
            <option value="progress">Progress</option>
            <option value="issue">Issue</option>
            <option value="completion">Completion</option>
          </select>
          <input value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)" data-testid="job-photo-caption"
            className="flex-1 min-w-[200px] h-9 px-3 text-sm bg-white border border-brand-linen" />
        </div>
        <label className="inline-flex items-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm cursor-pointer hover:bg-brand-navy/90"
          data-testid="job-photo-upload-btn">
          <Camera className="w-4 h-4" /> {saving ? "Uploading…" : "Take / choose photo"}
          <input type="file" accept="image/*" capture="environment" hidden onChange={upload} disabled={saving} />
        </label>
      </div>

      {loading ? <div className="text-sm text-brand-muted">Loading…</div> :
        items.length === 0 ? <EmptyState label="No photos yet." /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="job-photos-grid">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-brand-linen overflow-hidden" data-testid={`job-photo-${p.id}`}>
                {/* Photo tile */}
                <img src={p.url} alt={p.caption || "Job progress"} className="w-full h-40 object-cover" />
                <div className="p-2 space-y-1">
                  <div className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 ${
                    p.tag === "before" ? "bg-stone-100" : p.tag === "issue" ? "bg-rose-100 text-rose-800" :
                    p.tag === "completion" ? "bg-emerald-100 text-emerald-800" : "bg-brand-teal/15 text-brand-teal"}`}>
                    {p.tag}
                  </div>
                  {p.caption && <div className="text-xs text-brand-muted line-clamp-2">{p.caption}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// -- Variations -------------------------------------------------------------
function VariationsTab({ job }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.get(`/pro/jobs/${job.id}/variations`);
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  return (
    <div className="space-y-4" data-testid="job-tab-variations">
      <button onClick={() => setShowAdd(true)} data-testid="job-variation-add-btn"
        className="inline-flex items-center gap-1 px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90">
        <Plus className="w-4 h-4" /> Add variation
      </button>

      {showAdd && <AddVariationForm jobId={job.id} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}

      <p className="text-xs text-brand-muted max-w-2xl">
        Variations are extra work that never modify the original accepted quote.
        Send them for homeowner approval before invoicing.
      </p>

      {loading ? <div className="text-sm text-brand-muted">Loading…</div> :
        items.length === 0 ? <EmptyState label="No variations yet." /> : (
          <div className="space-y-2" data-testid="job-variations-list">
            {items.map((v) => (
              <div key={v.id} className="bg-white border border-brand-linen p-4" data-testid={`job-variation-${v.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-brand-muted">Variation #{v.variation_number} · {v.status}</div>
                    <div className="font-serif text-lg text-brand-ink">{v.title}</div>
                    {v.description && <div className="text-sm text-brand-muted mt-1">{v.description}</div>}
                  </div>
                  {v.amount_aud != null && (
                    <div className="text-right shrink-0">
                      <div className="font-serif text-xl text-brand-ink">${Number(v.amount_aud).toFixed(2)}</div>
                      <div className="text-[10px] text-brand-muted">{v.gst_inclusive ? "inc. GST" : "ex. GST"}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function AddVariationForm({ jobId, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", amount_aud: "", gst_inclusive: true });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!f.title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/pro/jobs/${jobId}/variations`, {
        title: f.title.trim(),
        description: f.description || undefined,
        amount_aud: f.amount_aud === "" ? undefined : Number(f.amount_aud),
        gst_inclusive: f.gst_inclusive,
      });
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white border border-brand-linen p-5 space-y-3" data-testid="job-variation-add-form">
      <div className="overline text-brand-muted">New variation</div>
      <Field label="Title" value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="var-title" />
      <div>
        <label className="block text-xs text-brand-muted mb-1">Description</label>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
          rows={3} data-testid="var-description"
          className="w-full p-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount (AUD)" value={f.amount_aud} onChange={(v) => setF({ ...f, amount_aud: v })} type="number" testid="var-amount" />
        <label className="inline-flex items-center gap-2 text-sm mt-6">
          <input type="checkbox" checked={f.gst_inclusive} onChange={(e) => setF({ ...f, gst_inclusive: e.target.checked })}
            data-testid="var-gst-inclusive" />
          GST inclusive
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving || !f.title.trim()} data-testid="var-save-btn"
          className="px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50">
          {saving ? "Saving…" : "Save variation"}
        </button>
        <button onClick={onClose} data-testid="var-cancel-btn"
          className="px-4 h-9 text-sm bg-white border border-brand-linen hover:border-brand-teal">Cancel</button>
      </div>
    </div>
  );
}

// -- Payments (Phase 3) -----------------------------------------------------
const PAYMENT_STATUS_STYLES = {
  not_requested: "bg-stone-100 text-stone-700",
  requested: "bg-amber-100 text-amber-800",
  overdue: "bg-rose-100 text-rose-800",
  marked_received: "bg-emerald-100 text-emerald-800",
};

const PAYMENT_STATUS_LABEL = {
  not_requested: "Not requested",
  requested: "Requested",
  overdue: "Overdue",
  marked_received: "Received",
};

const PAYMENT_METHOD_LABEL = {
  stripe_payment_link: "Stripe Payment Link",
  bank_transfer: "Bank transfer",
  external_invoice: "External invoice",
  cash: "Cash",
  cheque: "Cheque",
  other: "Other",
};

function PaymentsTab({ job, onReload }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_due_aud: 0, total_received_aud: 0, outstanding_aud: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.get(`/pro/jobs/${job.id}/payments`);
      setItems(r.data?.items || []);
      setSummary(r.data?.summary || { total_due_aud: 0, total_received_aud: 0, outstanding_aud: 0 });
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  async function requestPayment(p) {
    try {
      await api.post(`/pro/jobs/${job.id}/payments/${p.id}/request`);
      await reload(); await onReload();
    } catch (e) { alert(e?.response?.data?.detail || "Request failed"); }
  }

  async function markReceived(p) {
    const ref = window.prompt("Reference / receipt number (optional)");
    if (ref === null) return;
    const note = window.prompt("Internal note (optional)") || "";
    try {
      await api.post(`/pro/jobs/${job.id}/payments/${p.id}/mark-received`, {
        reference_or_receipt_number: ref || undefined,
        internal_note: note || undefined,
      });
      await reload(); await onReload();
    } catch (e) { alert(e?.response?.data?.detail || "Mark-received failed"); }
  }

  async function deletePayment(p) {
    if (!window.confirm(`Delete "${p.label || p.milestone}"?`)) return;
    try {
      await api.delete(`/pro/jobs/${job.id}/payments/${p.id}`);
      await reload(); await onReload();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  }

  return (
    <div className="space-y-6" data-testid="job-tab-payments">
      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryStat label="Total due" value={summary.total_due_aud} testid="payments-summary-total-due" />
        <SummaryStat label="Received" value={summary.total_received_aud} tint="emerald" testid="payments-summary-received" />
        <SummaryStat label="Outstanding" value={summary.outstanding_aud} tint={summary.outstanding_aud > 0 ? "amber" : "emerald"} testid="payments-summary-outstanding" />
      </div>

      <div>
        <button onClick={() => { setEditing(null); setShowAdd(true); }} data-testid="job-payment-add-btn"
          className="inline-flex items-center gap-1 px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90">
          <Plus className="w-4 h-4" /> Add payment milestone
        </button>
      </div>

      {(showAdd || editing) && (
        <PaymentForm
          jobId={job.id}
          initial={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); reload(); onReload(); }}
        />
      )}

      <p className="text-xs text-brand-muted max-w-3xl">
        ColourMatch never holds or routes payment money — you set up your own Stripe Payment Link,
        bank details or external invoice link, and share it with the homeowner. When the money arrives,
        mark the milestone as received here so the outstanding balance stays accurate.
      </p>

      {loading ? <div className="text-sm text-brand-muted">Loading payments…</div> :
        items.length === 0 ? <EmptyState label="No payment milestones yet." /> : (
          <div className="space-y-2" data-testid="job-payments-list">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-brand-linen p-4" data-testid={`job-payment-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wide font-medium ${PAYMENT_STATUS_STYLES[p.status]}`}
                        data-testid={`payment-status-${p.id}`}>
                        {PAYMENT_STATUS_LABEL[p.status] || p.status}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-brand-muted">{p.milestone}</span>
                      {p.due_date && <span className="text-xs text-brand-muted">Due {fmtDate(p.due_date)}</span>}
                    </div>
                    <div className="font-serif text-lg text-brand-ink">{p.label || `${p.milestone} payment`}</div>
                    {p.payment_method && (
                      <div className="text-xs text-brand-muted mt-1">
                        via {PAYMENT_METHOD_LABEL[p.payment_method] || p.payment_method}
                        {p.external_invoice_number && ` · Inv #${p.external_invoice_number}`}
                      </div>
                    )}
                    {p.payment_request_url && (
                      <a href={p.payment_request_url} target="_blank" rel="noreferrer"
                        className="text-xs text-brand-teal hover:underline mt-1 inline-block break-all"
                        data-testid={`payment-request-url-${p.id}`}>
                        {p.payment_request_url}
                      </a>
                    )}
                    {p.status === "marked_received" && (
                      <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 inline-block"
                        data-testid={`payment-marked-received-label-${p.id}`}>
                        ✓ Marked as received by the painter.
                        {p.marked_received_at && ` · ${fmtDate(p.marked_received_at)}`}
                        {p.reference_or_receipt_number && ` · Ref: ${p.reference_or_receipt_number}`}
                      </div>
                    )}
                    {p.internal_note && (
                      <div className="mt-2 text-xs text-brand-muted italic">Note: {p.internal_note}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-xl text-brand-ink" data-testid={`payment-amount-${p.id}`}>
                      ${Number(p.amount_aud || 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-brand-muted">{p.gst_inclusive ? "inc. GST" : "ex. GST"}</div>
                  </div>
                </div>

                {/* Action row */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.status === "not_requested" && (
                    <button onClick={() => requestPayment(p)}
                      data-testid={`payment-request-btn-${p.id}`}
                      className="inline-flex items-center gap-1 px-3 h-8 text-xs bg-white border border-brand-linen hover:border-brand-teal">
                      <Send className="w-3 h-3" /> Mark as requested
                    </button>
                  )}
                  {p.status !== "marked_received" && (
                    <button onClick={() => markReceived(p)}
                      data-testid={`payment-mark-received-btn-${p.id}`}
                      className="inline-flex items-center gap-1 px-3 h-8 text-xs bg-emerald-600 text-white hover:bg-emerald-700">
                      <CheckCheck className="w-3 h-3" /> Mark as received
                    </button>
                  )}
                  {p.status !== "marked_received" && (
                    <button onClick={() => setEditing(p)}
                      data-testid={`payment-edit-btn-${p.id}`}
                      className="inline-flex items-center gap-1 px-3 h-8 text-xs bg-white border border-brand-linen hover:border-brand-teal">
                      Edit
                    </button>
                  )}
                  {p.status !== "marked_received" && (
                    <button onClick={() => deletePayment(p)}
                      data-testid={`payment-delete-btn-${p.id}`}
                      className="inline-flex items-center gap-1 px-3 h-8 text-xs bg-white border border-brand-linen hover:border-rose-400 hover:text-rose-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function SummaryStat({ label, value, tint, testid }) {
  const tintClass = tint === "emerald" ? "text-emerald-700" : tint === "amber" ? "text-amber-800" : "text-brand-ink";
  return (
    <div className="bg-white border border-brand-linen p-4" data-testid={testid}>
      <div className="overline text-brand-muted mb-1">{label}</div>
      <div className={`font-serif text-2xl ${tintClass}`}>${Number(value || 0).toFixed(2)}</div>
    </div>
  );
}

function PaymentForm({ jobId, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial);
  const [f, setF] = useState({
    milestone: initial?.milestone || "deposit",
    label: initial?.label || "",
    amount_aud: initial?.amount_aud ?? "",
    gst_inclusive: initial?.gst_inclusive ?? true,
    due_date: initial?.due_date || "",
    payment_method: initial?.payment_method || "",
    payment_request_url: initial?.payment_request_url || "",
    external_invoice_number: initial?.external_invoice_number || "",
    external_invoice_url: initial?.external_invoice_url || "",
    internal_note: initial?.internal_note || "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (f.amount_aud === "" || Number(f.amount_aud) < 0) {
      alert("Amount is required and must be ≥ 0"); return;
    }
    setSaving(true);
    try {
      const payload = { ...f, amount_aud: Number(f.amount_aud) };
      // Strip empty strings so the server treats them as omitted.
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (isEdit) {
        await api.patch(`/pro/jobs/${jobId}/payments/${initial.id}`, payload);
      } else {
        await api.post(`/pro/jobs/${jobId}/payments`, payload);
      }
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white border border-brand-linen p-5 space-y-3" data-testid="job-payment-form">
      <div className="overline text-brand-muted">{isEdit ? "Edit payment" : "New payment milestone"}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-brand-muted mb-1">Milestone</label>
          <select value={f.milestone} onChange={(e) => setF({ ...f, milestone: e.target.value })}
            disabled={isEdit} data-testid="pay-milestone"
            className="w-full h-10 px-3 text-sm bg-white border border-brand-linen">
            <option value="deposit">Deposit</option>
            <option value="progress">Progress</option>
            <option value="final">Final</option>
            <option value="variation">Variation</option>
            <option value="other">Other</option>
          </select>
        </div>
        <Field label="Label" value={f.label} onChange={(v) => setF({ ...f, label: v })} testid="pay-label" />
        <Field label="Amount (AUD)" type="number" value={f.amount_aud} onChange={(v) => setF({ ...f, amount_aud: v })} testid="pay-amount" />
        <label className="inline-flex items-center gap-2 text-sm mt-6">
          <input type="checkbox" checked={f.gst_inclusive} onChange={(e) => setF({ ...f, gst_inclusive: e.target.checked })}
            data-testid="pay-gst-inclusive" />
          GST inclusive
        </label>
        <Field label="Due date" type="date" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} testid="pay-due-date" />
        <div>
          <label className="block text-xs text-brand-muted mb-1">Payment method</label>
          <select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}
            data-testid="pay-method" className="w-full h-10 px-3 text-sm bg-white border border-brand-linen">
            <option value="">— select —</option>
            <option value="stripe_payment_link">Stripe Payment Link</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="external_invoice">External invoice</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-brand-muted mb-1">Payment request URL (Stripe link, bank details link, invoice link)</label>
          <input value={f.payment_request_url} onChange={(e) => setF({ ...f, payment_request_url: e.target.value })}
            placeholder="https://buy.stripe.com/..." data-testid="pay-request-url"
            className="w-full h-10 px-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal" />
        </div>
        <Field label="Invoice number (yours)" value={f.external_invoice_number} onChange={(v) => setF({ ...f, external_invoice_number: v })} testid="pay-invoice-number" />
        <Field label="Invoice URL (yours)" value={f.external_invoice_url} onChange={(v) => setF({ ...f, external_invoice_url: v })} testid="pay-invoice-url" />
        <div className="md:col-span-2">
          <label className="block text-xs text-brand-muted mb-1">Internal note (painter-only)</label>
          <textarea value={f.internal_note} onChange={(e) => setF({ ...f, internal_note: e.target.value })}
            rows={2} data-testid="pay-internal-note"
            className="w-full p-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal" />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="pay-save-btn"
          className="px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50">
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Add payment")}
        </button>
        <button onClick={onClose} data-testid="pay-cancel-btn"
          className="px-4 h-9 text-sm bg-white border border-brand-linen hover:border-brand-teal">Cancel</button>
      </div>
    </div>
  );
}

// -- Completion (Phase 4) ---------------------------------------------------
function CompletionTab({ job }) {
  const [comp, setComp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shareToken, setShareToken] = useState(job.share_token || null);

  async function reload() {
    const r = await api.get(`/pro/jobs/${job.id}/completion`);
    setComp(r.data?.completion);
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  async function issueShareLink() {
    const r = await api.post(`/pro/jobs/${job.id}/share-token`);
    setShareToken(r.data?.share_token);
  }

  function toggleItem(id) {
    if (!comp) return;
    setComp({
      ...comp,
      checklist: comp.checklist.map((c) => c.id === id ? { ...c, checked: !c.checked } : c),
    });
  }

  async function save() {
    if (!comp) return;
    setSaving(true);
    try {
      const payload = {
        checklist: comp.checklist,
        site_cleaned: !!comp.site_cleaned,
        waste_removed: !!comp.waste_removed,
        walkthrough_completed: !!comp.walkthrough_completed,
      };
      await api.put(`/pro/jobs/${job.id}/completion`, payload);
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  if (!comp) return <div className="text-sm text-brand-muted">Loading completion…</div>;
  const shareBase = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || window.location.origin;
  const shareUrl = shareToken ? `${shareBase}/shared/jobs/${shareToken}` : null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="job-tab-completion">
      {/* Checklist */}
      <div className="bg-white border border-brand-linen p-5">
        <div className="overline text-brand-muted mb-3">Completion checklist</div>
        <div className="space-y-2" data-testid="completion-checklist">
          {comp.checklist?.map((c) => (
            <label key={c.id} className="flex items-start gap-3 text-sm cursor-pointer" data-testid={`completion-item-${c.id}`}>
              <input type="checkbox" checked={!!c.checked} onChange={() => toggleItem(c.id)}
                     className="mt-0.5" data-testid={`completion-item-checkbox-${c.id}`} />
              <span className={c.checked ? "text-brand-ink line-through" : "text-brand-ink"}>{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Booleans */}
      <div className="bg-white border border-brand-linen p-5 space-y-3">
        <div className="overline text-brand-muted">Practical completion</div>
        <BoolRow label="Site cleaned" value={comp.site_cleaned}
                 onChange={(v) => setComp({ ...comp, site_cleaned: v })} testid="completion-site-cleaned" />
        <BoolRow label="Waste removed" value={comp.waste_removed}
                 onChange={(v) => setComp({ ...comp, waste_removed: v })} testid="completion-waste-removed" />
        <BoolRow label="Walkthrough with homeowner completed" value={comp.walkthrough_completed}
                 onChange={(v) => setComp({ ...comp, walkthrough_completed: v })} testid="completion-walkthrough" />
      </div>

      <button onClick={save} disabled={saving} data-testid="completion-save-btn"
        className="px-5 h-10 bg-brand-navy text-white text-sm hover:bg-brand-navy/90 disabled:opacity-50">
        {saving ? "Saving…" : "Save completion"}
      </button>

      {/* Homeowner acknowledgement + share link */}
      <div className="bg-white border border-brand-linen p-5">
        <div className="overline text-brand-muted mb-2">Homeowner acknowledgement</div>
        {comp.homeowner_acknowledged ? (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2"
               data-testid="completion-homeowner-ack">
            ✓ Homeowner acknowledged
            {comp.homeowner_acknowledged_at && ` on ${fmtDate(comp.homeowner_acknowledged_at)}`}
            {comp.homeowner_acknowledged_by && ` (${comp.homeowner_acknowledged_by})`}
            {comp.acknowledgement_note && (
              <div className="mt-1 text-xs italic">&ldquo;{comp.acknowledgement_note}&rdquo;</div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-brand-muted">
              Share the job link with your homeowner so they can acknowledge practical completion online.
            </p>
            {!shareToken ? (
              <button onClick={issueShareLink} data-testid="completion-issue-share-btn"
                className="px-4 h-9 text-sm bg-brand-navy text-white hover:bg-brand-navy/90">
                Generate homeowner link
              </button>
            ) : (
              <div className="space-y-1">
                <div className="text-xs text-brand-muted">Share this link:</div>
                <input readOnly value={shareUrl}
                  data-testid="completion-share-url"
                  onFocus={(e) => e.target.select()}
                  className="w-full h-9 px-3 text-xs font-mono bg-stone-50 border border-brand-linen" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BoolRow({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between text-sm cursor-pointer">
      <span>{label}</span>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
             data-testid={testid} />
    </label>
  );
}

// -- Warranty (Phase 4) -----------------------------------------------------
function WarrantyTab({ job }) {
  const [w, setW] = useState(null);
  const [acl, setAcl] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function reload() {
    const r = await api.get(`/pro/jobs/${job.id}/warranty`);
    setW(r.data?.warranty || {
      workmanship_term_years: "", manufacturer_term_years: "",
      start_date: "", workmanship_expiry_date: "", manufacturer_expiry_date: "",
      conditions: "", exclusions: "",
      warranty_contact_name: "", warranty_contact_email: "", warranty_contact_phone: "",
    });
    setAcl(r.data?.acl_disclaimer || "");
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [job.id]);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...w };
      // Numbers
      ["workmanship_term_years", "manufacturer_term_years"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.put(`/pro/jobs/${job.id}/warranty`, payload);
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function downloadCertificate() {
    setDownloading(true);
    try {
      const r = await api.post(`/pro/jobs/${job.id}/warranty/certificate`, null, {
        responseType: "blob", timeout: 30000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `warranty-${job.job_number.toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { alert(e?.response?.data?.detail || "Download failed"); }
    finally { setDownloading(false); }
  }

  if (!w) return <div className="text-sm text-brand-muted">Loading warranty…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="job-tab-warranty">
      <p className="text-xs text-brand-muted">
        Warranty terms are configurable per job. Nothing is promised by default — set exactly what you offer this homeowner.
        The ACL disclaimer below is included on every certificate you generate.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Workmanship warranty (years)" type="number" value={w.workmanship_term_years ?? ""}
               onChange={(v) => setW({ ...w, workmanship_term_years: v })} testid="warranty-workmanship-years" />
        <Field label="Manufacturer / paint warranty (years)" type="number" value={w.manufacturer_term_years ?? ""}
               onChange={(v) => setW({ ...w, manufacturer_term_years: v })} testid="warranty-manufacturer-years" />
        <Field label="Start date" type="date" value={w.start_date || ""}
               onChange={(v) => setW({ ...w, start_date: v })} testid="warranty-start-date" />
        <Field label="Workmanship expiry" type="date" value={w.workmanship_expiry_date || ""}
               onChange={(v) => setW({ ...w, workmanship_expiry_date: v })} testid="warranty-workmanship-expiry" />
        <Field label="Manufacturer expiry" type="date" value={w.manufacturer_expiry_date || ""}
               onChange={(v) => setW({ ...w, manufacturer_expiry_date: v })} testid="warranty-manufacturer-expiry" />
      </div>

      <div>
        <label className="block text-xs text-brand-muted mb-1">Conditions</label>
        <textarea value={w.conditions || ""} onChange={(e) => setW({ ...w, conditions: e.target.value })}
                  rows={4} data-testid="warranty-conditions"
                  className="w-full p-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal" />
      </div>
      <div>
        <label className="block text-xs text-brand-muted mb-1">Exclusions</label>
        <textarea value={w.exclusions || ""} onChange={(e) => setW({ ...w, exclusions: e.target.value })}
                  rows={4} data-testid="warranty-exclusions"
                  className="w-full p-3 text-sm bg-white border border-brand-linen focus:outline-none focus:border-brand-teal" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Warranty contact name" value={w.warranty_contact_name || ""}
               onChange={(v) => setW({ ...w, warranty_contact_name: v })} testid="warranty-contact-name" />
        <Field label="Contact email" value={w.warranty_contact_email || ""}
               onChange={(v) => setW({ ...w, warranty_contact_email: v })} testid="warranty-contact-email" />
        <Field label="Contact phone" value={w.warranty_contact_phone || ""}
               onChange={(v) => setW({ ...w, warranty_contact_phone: v })} testid="warranty-contact-phone" />
      </div>

      {/* ACL disclaimer preview */}
      <div className="bg-stone-50 border border-brand-linen p-4 text-xs text-brand-muted italic"
           data-testid="warranty-acl-disclaimer">
        <div className="font-medium text-brand-ink not-italic mb-1">Australian Consumer Law</div>
        {acl}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving} data-testid="warranty-save-btn"
          className="px-5 h-10 bg-brand-navy text-white text-sm hover:bg-brand-navy/90 disabled:opacity-50">
          {saving ? "Saving…" : "Save warranty"}
        </button>
        <button onClick={downloadCertificate} disabled={downloading || !w.id}
          data-testid="warranty-download-pdf-btn"
          className="inline-flex items-center gap-2 px-5 h-10 bg-brand-teal text-white text-sm hover:bg-brand-teal/90 disabled:opacity-50">
          <Download className="w-4 h-4" /> {downloading ? "Generating…" : "Download certificate PDF"}
        </button>
      </div>
      {w.certificate_generated_at && (
        <div className="text-xs text-brand-muted">
          Last certificate generated: {fmtDateTime(w.certificate_generated_at)}
        </div>
      )}
    </div>
  );
}

// -- Activity ---------------------------------------------------------------
function ActivityTab({ job }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    api.get(`/pro/jobs/${job.id}/events`)
      .then((r) => { if (ok) setItems(r.data?.items || []); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [job.id]);

  if (loading) return <div className="text-sm text-brand-muted">Loading activity…</div>;
  if (items.length === 0) return <EmptyState label="No activity yet." />;

  return (
    <div className="border-l-2 border-brand-linen ml-2 space-y-4 pl-6 py-2" data-testid="job-tab-activity">
      {items.map((e) => (
        <div key={e.id} className="relative" data-testid={`job-event-${e.id}`}>
          <div className="absolute -left-[29px] top-1.5 w-2.5 h-2.5 rounded-full bg-brand-teal" />
          <div className="text-xs text-brand-muted">{fmtDateTime(e.created_at)}</div>
          <div className="text-sm text-brand-ink font-medium">{e.event_type.replace(/_/g, " ")}</div>
          {e.event_data && Object.keys(e.event_data).length > 0 && (
            <div className="text-xs text-brand-muted mt-1 font-mono truncate max-w-xl">
              {JSON.stringify(e.event_data)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ label }) {
  return <div className="border border-dashed border-brand-linen p-8 text-center text-sm text-brand-muted bg-white">{label}</div>;
}
